import {
  type RankedBarTableItem,
  getValueRange,
  largePriceFormatOptions,
} from "../../../components"
import type { UppsDatarefApi } from "../../../dataref"
import type { IsoDate, SourceName } from "../../../types"
import { isDefined } from "../../../utils"
import type {
  ApiLabPerfSalesContribution,
  LabPerfSalesContributionGroup,
  LabPerfSalesContributionUpp,
} from "./financialDataLabSalesContribution.model"

export const growthPriceOptions = {
  ...largePriceFormatOptions,
  signDisplay: "exceptZero" as const,
}

export type FormattedLabPerfSalesContributionUpp = {
  rankedBarItem: RankedBarTableItem
} & LabPerfSalesContributionUpp

export interface FormattedLabPerfSalesContributionGroup {
  uppCount: number
  growthContribution: number | null
  evolution?: number | null
  valueRange: ReturnType<typeof getValueRange>
  upps: FormattedLabPerfSalesContributionUpp[]
}

export interface FormattedLabPerfSalesContribution {
  totalUppCount: number
  growthContribution: number | null
  evolution?: number | null
  sources: SourceName[]
  periodEndDate?: IsoDate | null
  drivers: FormattedLabPerfSalesContributionGroup
  detractors: FormattedLabPerfSalesContributionGroup
}

const formatGroup = (
  group: LabPerfSalesContributionGroup,
  uppDataref: UppsDatarefApi,
): FormattedLabPerfSalesContributionGroup => {
  const upps = formatUpps({ upps: group.upps, uppDataref })
  const values = upps.map(({ rankedBarItem }) => rankedBarItem)

  return {
    uppCount: group.upps.length,
    growthContribution: group.growthContribution,
    evolution: group.sales?.evolution,
    valueRange: getValueRange(values),
    upps,
  }
}

interface FormatUppsArgs {
  upps: LabPerfSalesContributionUpp[]
  uppDataref: UppsDatarefApi
}

const formatUpps = ({ upps, uppDataref }: FormatUppsArgs) =>
  upps.toSorted(byContribution).map((upp, index) => ({
    ...upp,
    rankedBarItem: {
      rank: index + 1,
      name: getUppName(upp, uppDataref),
      weight: upp.growthContributionWeight ?? 0,
      value: upp.growthContribution ?? 0,
    } satisfies RankedBarTableItem,
  }))

export const getUppName = (
  upp: LabPerfSalesContributionUpp,
  uppDataref: UppsDatarefApi,
) => {
  if (!isDefined(upp.id)) return "Autres Pharmacies"
  const ref = uppDataref.find((ref) => ref.id === upp.id)
  if (!ref) return `UPP(${upp.id})`
  return ref.name
}

export const byContribution = (
  a: LabPerfSalesContributionUpp,
  b: LabPerfSalesContributionUpp,
) => {
  const aContribution = a.growthContribution
  const bContribution = b.growthContribution
  if (!isDefined(aContribution)) return isDefined(bContribution) ? 1 : 0
  if (!isDefined(bContribution)) return -1
  // absolute value: detractors carry negative sales
  return Math.abs(bContribution) - Math.abs(aContribution)
}

interface SelectLabPerfSalesContributionArgs {
  data: ApiLabPerfSalesContribution
  uppDataref: UppsDatarefApi
}

export const selectLabPerfSalesContribution = ({
  data,
  uppDataref,
}: SelectLabPerfSalesContributionArgs): FormattedLabPerfSalesContribution => ({
  totalUppCount: data.growthDrivers.upps.length + data.growthDetractors.upps.length,
  growthContribution: data.growthContribution,
  evolution: data.sales?.evolution,
  sources: data.sources,
  periodEndDate: data.periodEndDate,
  drivers: formatGroup(data.growthDrivers, uppDataref),
  detractors: formatGroup(data.growthDetractors, uppDataref),
})
